import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Logout03Icon, UserAdd02Icon } from "@hugeicons-pro/core-duotone-rounded";
import { useBrandingAssets } from "@/hooks/useBrandingAssets";
import { useAuth } from "@/contexts/AuthContext";
import type { PublicSidebarSectionId } from "@/utils/sidebarShellRoutes";
import { RailAccountMenu } from "./account-menu/RailAccountMenu";

// Nav/footer menu items are temporarily icon-less pending resolution of
// the Hugeicons Pro licensing/registry setup.

export type PublicSidebarSection = PublicSidebarSectionId;

interface PublicSidebarProps {
  // Absent on pages (e.g. legal pages) that use this shell but aren't one of
  // the rail's own nav items — the sidebar still renders, just with nothing
  // highlighted.
  active?: PublicSidebarSection;
}

export interface PublicSidebarNavItem {
  section: PublicSidebarSection;
  label: string;
  to: string;
}

// Exported so the mobile/tablet menu tray can render the same rail items
// without redefining them.
export const NAV_ITEMS: PublicSidebarNavItem[] = [
  { section: "home", label: "Home", to: "/" },
  { section: "music", label: "Music", to: "/songs" },
  { section: "playlists", label: "Playlists", to: "/playlists" },
  { section: "artists", label: "Artists", to: "/artists" },
  { section: "charts", label: "Charts", to: "/charts" },
  { section: "events", label: "Events", to: "/events" },
];

export const FOOTER_ITEMS: PublicSidebarNavItem[] = [
  { section: "plans", label: "Plans", to: "/plans" },
  { section: "sponsors", label: "Sponsors", to: "/sponsors" },
  { section: "support", label: "Support", to: "/support" },
];

function NigeriaFlagDisc() {
  return (
    <span
      className="h-[17px] w-[17px] shrink-0 overflow-hidden rounded-full block"
      aria-label="Nigeria"
    >
      <svg width="17" height="17" viewBox="0 0 24 24" className="block">
        <rect width="8" height="24" fill="#008751" />
        <rect x="8" width="8" height="24" fill="#FFFFFF" />
        <rect x="16" width="8" height="24" fill="#008751" />
      </svg>
    </span>
  );
}

function SidebarLink({
  item,
  active,
}: {
  item: (typeof NAV_ITEMS)[number];
  active: boolean;
}) {
  return (
    <Link
      to={item.to}
      className={`flex items-center gap-3 h-11 px-[22px] font-archivo text-sm transition-colors ${
        active
          ? "bg-[#5A31D8]/30 border-l-2 border-[#8B6BF0] text-white font-semibold"
          : "border-l-2 border-transparent text-[#9A96C4] font-medium hover:bg-white/[0.05] hover:text-white"
      }`}
    >
      {item.label}
    </Link>
  );
}

export function PublicSidebar({ active }: PublicSidebarProps) {
  const { logoUrl } = useBrandingAssets();
  const { user } = useAuth();

  return (
    <aside className="hidden lg:flex w-[264px] shrink-0 h-screen sticky top-0 self-start bg-[#0E0C24] border-r border-white/[0.08] flex-col py-[26px] font-archivo">
      <Link to="/" className="flex items-center gap-2.5 px-[22px] pb-7">
        <img
          src={logoUrl}
          alt="PayStreem"
          className="h-[30px] w-[30px] shrink-0 rounded-md object-cover"
        />
        <span className="font-black text-xl text-white tracking-tight">
          PayStreem
        </span>
        <NigeriaFlagDisc />
      </Link>

      <div className="flex flex-col">
        {NAV_ITEMS.map((item) => (
          <SidebarLink key={item.section} item={item} active={active === item.section} />
        ))}
      </div>

      <div className="h-px bg-white/10 mx-[22px] my-4" />

      <div className="flex flex-col">
        {FOOTER_ITEMS.map((item) => (
          <SidebarLink key={item.section} item={item} active={active === item.section} />
        ))}
      </div>

      <div className="mt-auto">
        {user ? (
          <RailAccountMenu />
        ) : (
          <div className="flex flex-col gap-[11px] px-[22px]">
            <Link
              to="/register"
              className="flex items-center justify-center gap-2 font-archivo font-bold text-sm text-[#1E1B4B] bg-white rounded-[2px] py-[15px]"
            >
              <HugeiconsIcon icon={UserAdd02Icon} size={16} color="#1E1B4B" />
              Start listening
            </Link>
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 font-archivo text-sm text-[#9A96C4] py-1"
            >
              <HugeiconsIcon icon={Logout03Icon} size={16} color="#9A96C4" />
              Log in
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
